import type { TextBoxColors } from 'atoms/TextBox'
import type {
  RowPaddings,
  SpecialRowContainerBackgroundT,
} from './RowContainer.h'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { ContainerDescriptionStyled } from 'blocks/Container/Description/ContainerDescription.styled'
import {
  BANNER_ASPECT_RATIO,
  BANNER_HEIGHT,
  BANNER_WIDTH,
  COLOR,
  FIXED_INDENTS,
  LAYOUT,
  MEDIA,
} from 'vars/index'
import {
  CellsAlignment,
  Gaps,
  RowControlsType,
  SpecialRowContainerBackground,
} from './RowContainer.h'

// block that indicates empty banners state
export const BannerCarouselEmptySC = styled.div`
  position: relative;
  width: 100%;
  height: 0;
`

export const BannerCarouselSC = styled.div<{
  controlsType: RowControlsType
  bannerWidth: BANNER_WIDTH
  cellsAlignment: CellsAlignment
  gap: (typeof Gaps)[keyof typeof Gaps]
  colorDefault?: TextBoxColors
  aspectRatio?: BANNER_ASPECT_RATIO
}>`
  display: flex;
  position: relative;
  overflow: hidden;
  width: 100%;
  --bannerPaginatorRowHeight: 0;

  &[data-has-pagination] {
    --bannerPaginatorRowHeight: calc(
      2 * var(${FIXED_INDENTS.INDENT_40}) + 28px
    );

    ${MEDIA.MAX_599} {
      --bannerPaginatorRowHeight: calc(
        2 * var(${FIXED_INDENTS.INDENT_30}) + 28px
      );
    }

    /*
     * Compact home shell (600–1023): dots sit on the 16:9 artwork and
     * description lives in the dark band below, so poster clearance is 0.
     */
    ${MEDIA.MIN_600} {
      ${MEDIA.MAX_1239} {
        --bannerPaginatorRowHeight: 0;
      }
    }

    ${MEDIA.MIN_1024} {
      --bannerPaginatorRowHeight: calc(
        2 * var(${FIXED_INDENTS.INDENT_40}) + 28px
      );
    }
  }

  --aspectRatio: ${({ aspectRatio }) =>
    `var(${aspectRatio ?? BANNER_ASPECT_RATIO.SIZE_10})`};
  height: max(
    calc(100vw / var(--aspectRatio)),
    var(${BANNER_HEIGHT.DETAILS_BANNER_COMPACT})
  );

  --leftControlPosition: ${({
    bannerWidth,
    controlsType,
    cellsAlignment,
    gap,
  }) =>
    controlsType === RowControlsType.inRowFluid
      ? '0px'
      : cellsAlignment !== CellsAlignment.Center ||
          bannerWidth === BANNER_WIDTH.SIZE_100 ||
          bannerWidth === BANNER_WIDTH.SIZE_94
        ? `calc(var(${LAYOUT.HorizontalPaddings}) + 1rem)`
        : bannerWidth === BANNER_WIDTH.SIZE_66
          ? `calc((100% - 66% )/2 - var(${gap}) - 1rem)`
          : `0`};
  --rightContolPosition: ${({
    bannerWidth,
    controlsType,
    cellsAlignment,
    gap,
  }) =>
    controlsType === RowControlsType.inRowFluid
      ? '0px'
      : cellsAlignment !== CellsAlignment.Center ||
          // ? `calc(var(${LAYOUT.HorizontalPaddings}) + 1rem)`
          bannerWidth === BANNER_WIDTH.SIZE_100 ||
          bannerWidth === BANNER_WIDTH.SIZE_94
        ? `calc(var(${LAYOUT.HorizontalPaddings}) + 1rem)`
        : bannerWidth === BANNER_WIDTH.SIZE_66
          ? `calc((100% - 66% )/2 - var(${gap}) - 1rem)`
          : `0`};
  color: ${({ colorDefault }) =>
    colorDefault ? `var(${colorDefault})` : 'inherit'};

  > ${ButtonSC} {
    position: absolute;
    z-index: 3;
    top: ${({ controlsType }) =>
      controlsType === RowControlsType.inRow ? '50%' : '0'};

    &:first-child {
      left: var(--leftControlPosition);
      transform: ${({ bannerWidth, cellsAlignment, controlsType }) =>
        controlsType === RowControlsType.inRow
          ? bannerWidth === BANNER_WIDTH.SIZE_66 &&
            cellsAlignment === CellsAlignment.Center
            ? 'translate(-100%, -50%)'
            : 'translate(0%, -50%)'
          : 'none'};
    }

    &:last-child {
      right: var(--rightContolPosition);
      transform: ${({ bannerWidth, cellsAlignment, controlsType }) =>
        controlsType === RowControlsType.inRow
          ? bannerWidth === BANNER_WIDTH.SIZE_66 &&
            cellsAlignment === CellsAlignment.Center
            ? 'translate(100%, -50%)'
            : 'translate(0%, -50%)'
          : 'none'};
    }
  }
`

export const BannerRowSC = styled.div<{
  bannerWidth: BANNER_WIDTH
  gap?: (typeof Gaps)[keyof typeof Gaps]
  activeSlide: number
  cellsAlignment: CellsAlignment
  scrollEnabled: boolean
  infiniteLoop: boolean
  visibleCards: number
}>`
  position: relative;
  width: 100%;
  height: 100%;
  --bannerWidth: ${({ bannerWidth }) => `var(${bannerWidth})`};

  transform: ${({ activeSlide, gap }) => `translateX(
      calc(
        -${activeSlide} *  (var(--bannerWidth) + var(${gap})) + (100%  - var(--bannerWidth) -  var(${gap})) / 2  + var(${gap})
      )
    )`};

  transition: ${({ scrollEnabled }) =>
    scrollEnabled ? '0.25s linear' : 'none'};

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none; // firefox

  display: flex;
  gap: ${({ gap }) => `var(${gap || Gaps.SIZE_40})`};

  > * {
    flex: 0 0 var(--bannerWidth);
  }
`

export const RowWrapperSC = styled.div<{
  paddings?: (typeof RowPaddings)[keyof typeof RowPaddings]
  background?: SpecialRowContainerBackgroundT
  imageUrl?: string
  colorDefault?: TextBoxColors
  margins?: Array<LAYOUT | FIXED_INDENTS>
}>`
  position: relative;
  margin: ${({ margins }) =>
    margins?.length ? margins.map((margin) => `var(${margin})`).join(' ') : 0};
  width: 100%;
  color: ${({ colorDefault }) =>
    colorDefault ? `var(${colorDefault})` : 'inherit'};
  background: ${({ background, imageUrl }) => {
    switch (background) {
      case SpecialRowContainerBackground.Gradient:
        return `var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.ACCENT_PRIMARY_GRADIENT})`
      case SpecialRowContainerBackground.GradientWithOverlay:
        return ` var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.MAIN_GRADIENT_90}),  var(${COLOR.ACCENT_PRIMARY_GRADIENT}) `
      case SpecialRowContainerBackground.FullWidthImage:
        return `var(${COLOR.MAIN_GRADIENT_90}), var(${COLOR.MAIN_GRADIENT_0}), url(${imageUrl}) no-repeat left/100%`
      case SpecialRowContainerBackground.HalfWidthImage:
        return `var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.MAIN_GRADIENT_270}) left/51%, var(${COLOR.MAIN_GRADIENT_90}) left/51%, url(${imageUrl}) no-repeat left/50% `
      default:
        return 'none'
    }
  }};

  ${ContainerDescriptionStyled} {
    ${MEDIA.MAX_599} {
      padding-left: var(${LAYOUT.HorizontalPaddings});
    }
  }
`
export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(${FIXED_INDENTS.INDENT_60});
`
